import React, { useState, useEffect } from 'react';
import { Campaign } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { useLocaleFormatters } from '../../utils/localeFormatters';

interface MatchApplicationFormProps {
  campaign: Campaign;
  onSubmit?: (matchId: string) => void;
  onCancel?: () => void;
}

export const MatchApplicationForm: React.FC<MatchApplicationFormProps> = ({
  campaign,
  onSubmit,
  onCancel
}) => {
  const { user } = useAuthStore();
  const { formatNumber, formatCurrency } = useLocaleFormatters();
  const [influencerProfile, setInfluencerProfile] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    proposedRate: '',
    message: '',
    portfolio: '',
    availabilityStart: '',
    availabilityEnd: ''
  });

  // Fetch influencer profile on component mount
  useEffect(() => {
    const fetchInfluencerProfile = async () => {
      if (!user?.userId) return;

      try {
        const response = await graphqlApi.getInfluencerByUserId(user.userId);
        if (response.success && response.data) {
          setInfluencerProfile(response.data);
        }
      } catch (error) {
        console.error('Error fetching influencer profile:', error);
      }
    };

    fetchInfluencerProfile();
  }, [user?.userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.message.trim()) {
        alert('Cover message is required');
        setIsSubmitting(false);
        return;
      }

      if (!influencerProfile?.influencerId) {
        alert('Please complete your influencer profile first');
        setIsSubmitting(false);
        return;
      }

      const response = await graphqlApi.createMatch({
        campaignId: campaign.campaignId!,
        influencerId: influencerProfile.influencerId,
        proposedRate: formData.proposedRate ? parseFloat(formData.proposedRate) : undefined,
        coverLetter: formData.message  // Map old message field to new coverLetter field
      });

      if (response.success && response.data) {
        onSubmit?.(response.data.matchId);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Apply to Campaign
        </h3>
        <p className="text-sm text-gray-600">
          Submit your application for "{campaign.title}"
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Proposed Rate */}
        <div>
          <label htmlFor="proposedRate" className="block text-sm font-medium text-gray-700 mb-2">
            Proposed Rate (USD) *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              id="proposedRate"
              required
              min="0"
              step="0.01"
              className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="0.00"
              value={formData.proposedRate}
              onChange={(e) => handleInputChange('proposedRate', e.target.value)}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Your proposed rate for this campaign
          </p>
        </div>

        {/* Cover Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Cover Message *
          </label>
          <textarea
            id="message"
            required
            rows={4}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Tell the host why you're perfect for this campaign..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Explain your interest and relevant experience (max 500 characters)
          </p>
        </div>

        {/* Portfolio Link */}
        <div>
          <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700 mb-2">
            Portfolio/Work Examples
          </label>
          <input
            type="url"
            id="portfolio"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="https://example.com/portfolio"
            value={formData.portfolio}
            onChange={(e) => handleInputChange('portfolio', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Link to relevant work samples or portfolio
          </p>
        </div>

        {/* Availability */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="availabilityStart" className="block text-sm font-medium text-gray-700 mb-2">
              Available From
            </label>
            <input
              type="date"
              id="availabilityStart"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.availabilityStart}
              onChange={(e) => handleInputChange('availabilityStart', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="availabilityEnd" className="block text-sm font-medium text-gray-700 mb-2">
              Available Until
            </label>
            <input
              type="date"
              id="availabilityEnd"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.availabilityEnd}
              onChange={(e) => handleInputChange('availabilityEnd', e.target.value)}
            />
          </div>
        </div>

        {/* Campaign Requirements Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Campaign Requirements</h4>
          <div className="space-y-1 text-xs text-gray-600">
            {campaign.requirements?.minFollowers && (
              <p>• Minimum {formatNumber(campaign.requirements.minFollowers)} followers</p>
            )}
            {campaign.requirements?.minEngagementRate && (
              <p>• Minimum {campaign.requirements.minEngagementRate}% engagement rate</p>
            )}
            {campaign.budget && (
              <p>• Budget: {formatCurrency(campaign.budget, 'USD')}</p>
            )}
            {campaign.timeline && (
              <p>• Timeline: {campaign.timeline.startDate} to {campaign.timeline.endDate}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting Application...
              </span>
            ) : (
              'Submit Application'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 sm:flex-none bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
